import React, { useState, useEffect } from "react";
import { Plus, Building2, Pencil, CalendarOff, Trash2 } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import {
  facilities as api,
  type Facility,
  type FacilityBlackoutDate,
  type ApiError,
} from "../../services/api";
import DataGrid from "../../components/DataGrid";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};
const actionBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px 6px",
  borderRadius: 6,
  color: "var(--nx-text-muted)",
  display: "inline-flex",
  alignItems: "center",
};

type TimeWindowForm = { openTime: string; closeTime: string };

const INIT = {
  name: "",
  type: "gym",
  capacity: "",
  bookingSlotsPerDay: "0",
  openTime: "08:00",
  closeTime: "20:00",
  slotDurationMinutes: "60",
  bookingFee: "0",
  depositAmount: "0",
  peakStartTime: "",
  peakEndTime: "",
  peakBookingFee: "",
  cooldownMinutes: "0",
  isActive: "active",
  additionalWindows: [] as TimeWindowForm[],
};

const FACILITY_TYPES = [
  "gym",
  "pool",
  "clubhouse",
  "court",
  "hall",
  "garden",
  "other",
];

type FormState = typeof INIT;

const toRequest = (form: FormState) => ({
  name: form.name,
  type: form.type,
  capacity: form.capacity ? Number(form.capacity) : undefined,
  bookingSlotsPerDay: Number(form.bookingSlotsPerDay),
  openTime: form.openTime || undefined,
  closeTime: form.closeTime || undefined,
  slotDurationMinutes: Number(form.slotDurationMinutes),
  bookingFee: Number(form.bookingFee),
  depositAmount: Number(form.depositAmount),
  peakStartTime: form.peakStartTime || undefined,
  peakEndTime: form.peakEndTime || undefined,
  peakBookingFee: form.peakBookingFee ? Number(form.peakBookingFee) : undefined,
  cooldownMinutes: form.cooldownMinutes ? Number(form.cooldownMinutes) : 0,
  active: form.isActive === "active",
  additionalWindows: form.additionalWindows.filter(
    (w) => w.openTime && w.closeTime,
  ),
});

const FacilitiesPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const [items, setItems] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);

  // create
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState<FormState>(INIT);

  // edit
  const [editTarget, setEditTarget] = useState<Facility | null>(null);
  const [editForm, setEditForm] = useState<FormState>(INIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  // blackout dates
  const [blackoutTarget, setBlackoutTarget] = useState<Facility | null>(null);
  const [blackoutDates, setBlackoutDates] = useState<FacilityBlackoutDate[]>(
    [],
  );
  const [blackoutLoading, setBlackoutLoading] = useState(false);
  const [newBlackoutDate, setNewBlackoutDate] = useState("");
  const [newBlackoutReason, setNewBlackoutReason] = useState("");
  const [blackoutSaving, setBlackoutSaving] = useState(false);
  const [blackoutError, setBlackoutError] = useState("");
  const [deleteBlackoutTarget, setDeleteBlackoutTarget] =
    useState<FacilityBlackoutDate | null>(null);
  const [deletingBlackout, setDeletingBlackout] = useState(false);

  // delete facility
  const [deleteTarget, setDeleteTarget] = useState<Facility | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!societyId) {
      setLoading(false);
      return;
    }
    api
      .list(societyId)
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [societyId]);

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const changeEdit = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => setEditForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true);
    setFormError("");
    try {
      const created = await api.create(societyId, toRequest(form));
      setItems((p) => [created, ...p]);
      setShowForm(false);
      setForm(INIT);
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to add facility");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (f: Facility) => {
    setEditTarget(f);
    setEditForm({
      name: f.name,
      type: f.type,
      capacity: f.capacity != null ? String(f.capacity) : "",
      bookingSlotsPerDay: String(f.bookingSlotsPerDay),
      openTime: f.openTime ?? "08:00",
      closeTime: f.closeTime ?? "20:00",
      slotDurationMinutes: String(f.slotDurationMinutes ?? 60),
      bookingFee: String(f.bookingFee ?? 0),
      depositAmount: String(f.depositAmount ?? 0),
      peakStartTime: f.peakStartTime ?? "",
      peakEndTime: f.peakEndTime ?? "",
      peakBookingFee: f.peakBookingFee != null ? String(f.peakBookingFee) : "",
      cooldownMinutes: String(f.cooldownMinutes ?? 0),
      isActive: f.active ? "active" : "inactive",
      additionalWindows: (f.additionalWindows ?? []).map((w) => ({
        openTime: w.openTime,
        closeTime: w.closeTime,
      })),
    });
    setEditError("");
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !editTarget) return;
    setEditSaving(true);
    setEditError("");
    try {
      const updated = await api.update(
        societyId,
        editTarget.id,
        toRequest(editForm),
      );
      setItems((p) => p.map((f) => (f.id === editTarget.id ? updated : f)));
      setEditTarget(null);
    } catch (err) {
      setEditError((err as ApiError).message || "Failed to update facility");
    } finally {
      setEditSaving(false);
    }
  };

  const openBlackout = (f: Facility) => {
    setBlackoutTarget(f);
    setBlackoutError("");
    setNewBlackoutDate("");
    setNewBlackoutReason("");
    setBlackoutLoading(true);
    if (!societyId) return;
    api
      .listBlackoutDates(societyId, f.id)
      .then(setBlackoutDates)
      .catch(() => {})
      .finally(() => setBlackoutLoading(false));
  };

  const addBlackout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !blackoutTarget || !newBlackoutDate) return;
    setBlackoutSaving(true);
    setBlackoutError("");
    try {
      const created = await api.addBlackoutDate(societyId, blackoutTarget.id, {
        blackoutDate: newBlackoutDate,
        reason: newBlackoutReason || undefined,
      });
      setBlackoutDates((p) => [created, ...p]);
      setNewBlackoutDate("");
      setNewBlackoutReason("");
    } catch (err) {
      setBlackoutError(
        (err as ApiError).message || "Failed to add blackout date",
      );
    } finally {
      setBlackoutSaving(false);
    }
  };

  const confirmDeleteFacility = async () => {
    if (!societyId || !deleteTarget) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await api.delete(societyId, deleteTarget.id);
      setItems((p) => p.filter((f) => f.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError((err as ApiError).message || "Failed to delete facility.");
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteBlackout = async () => {
    if (!societyId || !blackoutTarget || !deleteBlackoutTarget) return;
    setDeletingBlackout(true);
    try {
      await api.deleteBlackoutDate(
        societyId,
        blackoutTarget.id,
        deleteBlackoutTarget.id,
      );
      setBlackoutDates((p) =>
        p.filter((b) => b.id !== deleteBlackoutTarget.id),
      );
      setDeleteBlackoutTarget(null);
    } catch {
      /* ignore */
    } finally {
      setDeletingBlackout(false);
    }
  };

  const typeColor = (t: string) => {
    const map: Record<string, { bg: string; text: string }> = {
      gym: { bg: "rgba(26,108,246,0.1)", text: "#1A6CF6" },
      pool: { bg: "rgba(6,182,212,0.1)", text: "#06B6D4" },
      clubhouse: { bg: "rgba(109,40,217,0.1)", text: "#6D28D9" },
      court: { bg: "rgba(16,185,129,0.1)", text: "#10B981" },
    };
    return map[t] || { bg: "rgba(148,163,184,0.1)", text: "#64748B" };
  };

  const fmtAmount = (n: number | undefined) =>
    `₹${(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  const renderConfigFields = (
    f: FormState,
    onChange: typeof change,
    setF: React.Dispatch<React.SetStateAction<FormState>>,
  ) => (
    <>
      <div>
        <label style={lbl}>Facility Name *</label>
        <input
          name="name"
          value={f.name}
          onChange={onChange}
          required
          placeholder="Swimming Pool"
          style={inp}
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={lbl}>Type *</label>
          <select name="type" value={f.type} onChange={onChange} style={inp}>
            {FACILITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={lbl}>Status</label>
          <select
            name="isActive"
            value={f.isActive}
            onChange={onChange}
            style={inp}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Capacity</label>
          <input
            type="number"
            name="capacity"
            value={f.capacity}
            onChange={onChange}
            placeholder="50"
            min="0"
            style={inp}
          />
        </div>
      </div>
      <span
        style={{
          display: "block",
          fontSize: 11,
          color: "var(--nx-text-muted)",
          marginBottom: 6,
        }}
      >
        All times below use 24-hour format (e.g. 18:00 = 6:00 PM).
      </span>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={lbl}>Open Time (24-hr)</label>
          <input
            type="time"
            name="openTime"
            value={f.openTime}
            onChange={onChange}
            style={inp}
          />
        </div>
        <div>
          <label style={lbl}>Close Time (24-hr)</label>
          <input
            type="time"
            name="closeTime"
            value={f.closeTime}
            onChange={onChange}
            style={inp}
          />
        </div>
      </div>
      <div>
        <label style={lbl}>Slot Duration (minutes)</label>
        <input
          type="number"
          name="slotDurationMinutes"
          value={f.slotDurationMinutes}
          onChange={onChange}
          min="15"
          step="15"
          style={inp}
        />
      </div>
      <div style={{ marginBottom: 16, marginTop: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: f.additionalWindows.length ? 8 : 0 }}>
          <span
            style={{
              fontSize: 12,
              color: "var(--nx-text-muted)",
              lineHeight: 1.4,
              flex: 1,
            }}
          >
            The main open/close range above is one bookable window. Add more for
            split timings, e.g. a morning and an evening slot.
          </span>
          <button
            type="button"
            onClick={() =>
              setF((p) => ({
                ...p,
                additionalWindows: [...p.additionalWindows, { openTime: "", closeTime: "" }],
              }))
            }
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid var(--nx-border)",
              background: "var(--nx-surface-3)",
              color: "var(--nx-text-secondary)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            + Add timing
          </button>
        </div>
        {f.additionalWindows.map((w, idx) => (
          <div
            key={idx}
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 8, alignItems: "end" }}
          >
            <div>
              <label style={lbl}>Open Time</label>
              <input
                type="time"
                value={w.openTime}
                onChange={(e) =>
                  setF((p) => ({
                    ...p,
                    additionalWindows: p.additionalWindows.map((row, i) =>
                      i === idx ? { ...row, openTime: e.target.value } : row,
                    ),
                  }))
                }
                style={inp}
              />
            </div>
            <div>
              <label style={lbl}>Close Time</label>
              <input
                type="time"
                value={w.closeTime}
                onChange={(e) =>
                  setF((p) => ({
                    ...p,
                    additionalWindows: p.additionalWindows.map((row, i) =>
                      i === idx ? { ...row, closeTime: e.target.value } : row,
                    ),
                  }))
                }
                style={inp}
              />
            </div>
            <button
              type="button"
              onClick={() =>
                setF((p) => ({
                  ...p,
                  additionalWindows: p.additionalWindows.filter((_, i) => i !== idx),
                }))
              }
              title="Remove this timing"
              style={{ ...actionBtn, color: "var(--nx-red)", height: 38 }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={lbl}>Booking Fee (₹)</label>
          <input
            type="number"
            name="bookingFee"
            value={f.bookingFee}
            onChange={onChange}
            min="0"
            style={inp}
          />
        </div>
        <div>
          <label style={lbl}>Deposit Amount (₹)</label>
          <input
            type="number"
            name="depositAmount"
            value={f.depositAmount}
            onChange={onChange}
            min="0"
            style={inp}
          />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={lbl}>Peak Start Time</label>
          <input
            type="time"
            name="peakStartTime"
            value={f.peakStartTime}
            onChange={onChange}
            style={inp}
          />
        </div>
        <div>
          <label style={lbl}>Peak End Time</label>
          <input
            type="time"
            name="peakEndTime"
            value={f.peakEndTime}
            onChange={onChange}
            style={inp}
          />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={lbl}>Peak Booking Fee (₹)</label>
          <input
            type="number"
            name="peakBookingFee"
            value={f.peakBookingFee}
            onChange={onChange}
            min="0"
            placeholder="Leave blank for flat fee"
            style={inp}
          />
        </div>
        <div>
          <label style={lbl}>Booking Cool-down (min)</label>
          <input
            type="number"
            name="cooldownMinutes"
            value={f.cooldownMinutes}
            onChange={onChange}
            min="0"
            style={inp}
          />
        </div>
      </div>
    </>
  );

  return (
    <PageLayout>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 22,
              fontWeight: 700,
              color: "var(--nx-text-primary)",
              letterSpacing: "-0.03em",
              marginBottom: 4,
            }}
          >
            Facilities
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {items.length} facilities registered
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="nx-btn nx-btn-primary"
          style={{ gap: 6 }}
        >
          <Plus size={14} /> Add Facility
        </button>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataGrid
          columns={[
            {
              key: "name",
              header: "Name",
              cellStyle: { fontWeight: 500 },
              render: (f: Facility) => f.name,
            },
            {
              key: "type",
              header: "Type",
              render: (f: Facility) => {
                const tc = typeColor(f.type);
                return (
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 99,
                      fontSize: 10,
                      fontWeight: 500,
                      background: tc.bg,
                      color: tc.text,
                    }}
                  >
                    {f.type.charAt(0).toUpperCase() + f.type.slice(1)}
                  </span>
                );
              },
            },
            {
              key: "hours",
              header: "Hours",
              cellStyle: { color: "var(--nx-text-secondary)" },
              render: (f: Facility) => {
                if (!f.openTime || !f.closeTime) return "—";
                const extra = f.additionalWindows?.length ?? 0;
                return `${f.openTime}–${f.closeTime}${extra ? ` +${extra}` : ""}`;
              },
            },
            {
              key: "fee",
              header: "Fee / Deposit",
              cellStyle: { color: "var(--nx-text-secondary)" },
              render: (f: Facility) =>
                `${fmtAmount(f.bookingFee)} / ${fmtAmount(f.depositAmount)}`,
            },
            {
              key: "status",
              header: "Status",
              render: (f: Facility) => (
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: 99,
                    fontSize: 10,
                    fontWeight: 500,
                    background: f.active
                      ? "rgba(16,185,129,0.1)"
                      : "rgba(148,163,184,0.1)",
                    color: f.active ? "#10B981" : "#64748B",
                  }}
                >
                  {f.active ? "Active" : "Inactive"}
                </span>
              ),
            },
            {
              key: "actions",
              header: "",
              render: (f: Facility) => (
                <div
                  style={{
                    display: "flex",
                    gap: 4,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    style={actionBtn}
                    onClick={() => openEdit(f)}
                    title="Edit facility"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    style={actionBtn}
                    onClick={() => openBlackout(f)}
                    title="Blackout dates"
                  >
                    <CalendarOff size={14} />
                  </button>
                  <button
                    style={{ ...actionBtn, color: "var(--nx-red)" }}
                    onClick={() => {
                      setDeleteTarget(f);
                      setDeleteError("");
                    }}
                    title="Delete facility"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={(f) => f.id}
          loading={loading}
          emptyIcon={
            <Building2 size={32} style={{ color: "var(--nx-text-muted)" }} />
          }
          emptyText="No facilities added yet."
        />
      </div>

      {showForm && (
        <Modal
          title="Add Facility"
          submitLabel="Add Facility"
          error={formError}
          isLoading={saving}
          onClose={() => {
            setShowForm(false);
            setForm(INIT);
            setFormError("");
          }}
          onSubmit={handleSubmit}
        >
          {renderConfigFields(form, change, setForm)}
        </Modal>
      )}

      {editTarget && (
        <Modal
          title={`Edit ${editTarget.name}`}
          submitLabel="Save Changes"
          error={editError}
          isLoading={editSaving}
          onClose={() => setEditTarget(null)}
          onSubmit={handleEdit}
        >
          {renderConfigFields(editForm, changeEdit, setEditForm)}
        </Modal>
      )}

      {blackoutTarget && (
        <Modal
          title={`Blackout Dates — ${blackoutTarget.name}`}
          submitLabel="Add Date"
          error={blackoutError}
          isLoading={blackoutSaving}
          onClose={() => setBlackoutTarget(null)}
          onSubmit={addBlackout}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={lbl}>Date *</label>
              <input
                type="date"
                value={newBlackoutDate}
                onChange={(e) => setNewBlackoutDate(e.target.value)}
                required
                style={inp}
              />
            </div>
            <div>
              <label style={lbl}>Reason</label>
              <input
                value={newBlackoutReason}
                onChange={(e) => setNewBlackoutReason(e.target.value)}
                placeholder="Maintenance"
                style={inp}
              />
            </div>
          </div>
          <div>
            <label style={lbl}>Existing Blackout Dates</label>
            {blackoutLoading ? (
              <p style={{ fontSize: 12, color: "var(--nx-text-muted)" }}>
                Loading…
              </p>
            ) : blackoutDates.length === 0 ? (
              <p style={{ fontSize: 12, color: "var(--nx-text-muted)" }}>
                None yet.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {blackoutDates.map((b) => (
                  <div
                    key={b.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 10px",
                      background: "var(--nx-surface-3)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  >
                    <span>
                      {b.blackoutDate}
                      {b.reason ? ` — ${b.reason}` : ""}
                    </span>
                    <button
                      type="button"
                      style={actionBtn}
                      onClick={() => setDeleteBlackoutTarget(b)}
                      title="Remove"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={!!deleteBlackoutTarget}
        variant="danger"
        title="Remove Blackout Date"
        message={`Remove the blackout on ${deleteBlackoutTarget?.blackoutDate}? Residents will be able to book this date again.`}
        confirmLabel="Remove"
        isLoading={deletingBlackout}
        onConfirm={confirmDeleteBlackout}
        onCancel={() => setDeleteBlackoutTarget(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        variant="danger"
        title={`Delete "${deleteTarget?.name}"?`}
        message="This facility will be permanently removed. Facilities with existing bookings cannot be deleted."
        confirmLabel="Delete Facility"
        isLoading={deleting}
        error={deleteError}
        onConfirm={confirmDeleteFacility}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteError("");
        }}
      />
    </PageLayout>
  );
};

export default FacilitiesPage;
